import { NetworkStatus, useApolloClient, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useMemo, useRef } from "react";
import { USER_PERMISSIONS } from "config/constants";
import {
  ACTIVATE_VOUCHER,
  DEACTIVATE_VOUCHER,
  GENERATE_PROMOTION_PREVIEW,
  GET_CHANGE_ACTIVE_STATUS,
} from "graphql/campaign/mutation";
import {
  CHECK_DUPLICATE_CODES,
  COUNT_VOUCHERS,
  GET_DETAIL_PROMOTION_CAMPAIGN,
  GET_LIST_CAMPAIGN_QUERY,
  GET_ORDER_IN_PROMO_TABLE,
  GET_SELLER_NAME_QUERY,
  GET_SELLER_RANKING_LIST,
  GET_SUMMARY_TABS,
  GET_VOUCHERS_CAMPAIGN,
  GET_VOUCHERS_CAMPAIGN_CLONE,
  SHOW_VOUCHER_CODES,
  GET_IMPORT_FILE_CAMPAIGN,
} from "graphql/campaign/query";
import { useGetUserPermissions } from "hooks/user/user";
import { hasPermission } from "utils/helperFuncs";
import { convertDataOrderInPromo, convertGetListCampaign, convertGetSellerName } from "./converter";

/**
 * cache-first lần đầu; đã có cache → cache-and-network (hiện cache ngay, fetch nền).
 * ⚠ Stabilize: policy được "đóng băng" sau lần xác định đầu tiên để tránh useQuery
 * refetch liên tục do object reference thay đổi (gây loop GetDetailPromotionCampaign).
 * Không dùng nextFetchPolicy — nó khiến Apollo tự chuyển fetchPolicy sau lần fetch đầu,
 * gây thêm 1 request nữa.
 */
function useStaleFetchPolicy(query, variables, { skip = false } = {}) {
  const client = useApolloClient();

  const hasCachedData = useMemo(() => {
    if (skip) return false;
    try {
      const cached = client.readQuery({ query, variables });
      return Boolean(cached);
    } catch {
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, query, JSON.stringify(variables), skip]);

  // Đóng băng policy sau lần xác định đầu tiên — tránh refetch loop
  const frozenRef = useRef(null);
  if (!frozenRef.current) {
    frozenRef.current = hasCachedData ? { fetchPolicy: "cache-and-network" } : { fetchPolicy: "cache-first" };
  }
  return frozenRef.current;
}

function updateCampaignActiveStatusInCache(cache, promotionID, isActive) {
  cache.modify({
    id: cache.identify({ __typename: "PromotionCampaign", id: promotionID }),
    fields: {
      isActive() {
        return isActive;
      },
    },
  });
}

function normalizeListCampaignVariables(params) {
  const { filters = {}, pagination, sort } = params ?? {};

  return {
    filters: {
      timeRange: filters.timeRange ?? {},
      query: filters.query ?? undefined,
      types: filters.types ?? [],
      saleChannelIDs: filters.saleChannelIDs ?? [],
      categoryIDs: filters.categoryIDs ?? [],
      brandIDs: filters.brandIDs ?? [],
      statuses: filters.statuses ?? [],
      isActive: filters.isActive ?? undefined,
      createdByIDs: filters.createdByIDs ?? undefined,
    },
    pagination,
    sort,
  };
}

export const useGetListCampaign = (params) => {
  const variables = useMemo(() => normalizeListCampaignVariables(params), [params]);
  const listFetchPolicy = useStaleFetchPolicy(GET_LIST_CAMPAIGN_QUERY, variables);
  const apolloResult = useQuery(GET_LIST_CAMPAIGN_QUERY, {
    variables,
    notifyOnNetworkStatusChange: true,
    ...listFetchPolicy,
  });

  const isInitialLoading =
    (apolloResult.networkStatus === NetworkStatus.loading ||
      apolloResult.networkStatus === NetworkStatus.setVariables) &&
    !apolloResult.data;

  return {
    refetch: (options) =>
      apolloResult.refetch({
        fetchPolicy: "network-only",
        ...options,
      }),
    loading: isInitialLoading,
    error: apolloResult.error,
    data: convertGetListCampaign(apolloResult.data?.promotionCampaign?.pagination?.promotionCampaigns) || [],
    paginationData: apolloResult.data?.promotionCampaign?.pagination?.paginationData,
  };
};

export const useGetSummaryTabs = () => {
  const summaryFetchPolicy = useStaleFetchPolicy(GET_SUMMARY_TABS);
  const apolloResult = useQuery(GET_SUMMARY_TABS, {
    notifyOnNetworkStatusChange: true,
    ...summaryFetchPolicy,
  });

  const isInitialLoading = apolloResult.networkStatus === NetworkStatus.loading && !apolloResult.data;

  return {
    data: apolloResult.data,
    loading: isInitialLoading,
    refetch: (options) =>
      apolloResult.refetch({
        fetchPolicy: "network-only",
        ...options,
      }),
  };
};

export const useGetChangeActiveStatus = () => {
  const [updateStatus, { loading, error }] = useMutation(GET_CHANGE_ACTIVE_STATUS, {
    update(cache, { data }, { variables }) {
      const { promotionID, isActive } = variables;
      const result = data?.promotionCampaign?.changeActiveStatus?.result;
      if (!result) return;

      try {
        updateCampaignActiveStatusInCache(cache, promotionID, isActive);
      } catch {
        // Entity chưa normalize trong cache — refetch sẽ cập nhật sau
      }
    },
    refetchQueries: ["GetCampaignList", "CountCampaign"],
    awaitRefetchQueries: false,
  });
  const handleUpdateStatus = async (params) => {
    return await updateStatus({
      variables: params,
    });
  };

  return {
    handleUpdateStatus,
    loading,
    error,
  };
};

export const useGetSellerRankingList = (params) => {
  const { data, loading, error, refetch } = useQuery(GET_SELLER_RANKING_LIST, {
    variables: params,
    skip: params === undefined,
  });

  return {
    loading,
    error,
    refetch,
    data: data?.promotionCampaign?.sellerRankingList,
    paginationData: data?.promotionCampaign?.sellerRankingList?.paginationData,
  };
};

export const useGetOrderInPromo = (params) => {
  const { data, loading, error, refetch } = useQuery(GET_ORDER_IN_PROMO_TABLE, {
    variables: params,
  });

  return {
    loading,
    error,
    refetch,
    data: convertDataOrderInPromo(data?.promotionCampaign?.orderRankingList?.ordersRanking) || [],
    paginationData: data?.promotionCampaign?.orderRankingList?.paginationData,
  };
};

export const useGetSellerName = (params) => {
  const { loading, data, refetch, fetchMore } = useQuery(GET_SELLER_NAME_QUERY, {
    variables: params,
    fetchPolicy: "cache-first",
  });
  return {
    loading,
    data: convertGetSellerName(data?.seller?.pagination?.sellers) || [],
    paginationData: data?.seller?.pagination?.paginationData,
    refetch,
    fetchMore,
  };
};

export const useGetDetailPromotionCampaign = (params) => {
  const { campaignType: knownCampaignType, ...queryVariables } = params ?? {};

  const bootstrapSkipped = Boolean(knownCampaignType);
  const bootstrapQuery = GET_DETAIL_PROMOTION_CAMPAIGN(false);
  const bootstrapFetchPolicy = useStaleFetchPolicy(bootstrapQuery, queryVariables, {
    skip: bootstrapSkipped,
  });

  const {
    data: bootstrapData,
    loading: bootstrapLoading,
    refetch: refetchBootstrap,
  } = useQuery(bootstrapQuery, {
    variables: queryVariables,
    skip: bootstrapSkipped,
    notifyOnNetworkStatusChange: true,
    ...bootstrapFetchPolicy,
  });

  const resolvedCampaignType = knownCampaignType || bootstrapData?.promotionCampaign?.get?.campaignType;
  const isProductPromo = resolvedCampaignType === "PRODUCT_PROMO";

  const runProductPromoQuery =
    isProductPromo && (Boolean(knownCampaignType) || Boolean(bootstrapData?.promotionCampaign?.get));
  const runStandardKnownQuery = Boolean(knownCampaignType) && !isProductPromo;

  const productPromoQuery = GET_DETAIL_PROMOTION_CAMPAIGN(true);
  const productPromoFetchPolicy = useStaleFetchPolicy(productPromoQuery, queryVariables, {
    skip: !runProductPromoQuery,
  });

  const {
    data: productPromoData,
    loading: productPromoLoading,
    refetch: refetchProductPromo,
  } = useQuery(productPromoQuery, {
    variables: queryVariables,
    skip: !runProductPromoQuery,
    notifyOnNetworkStatusChange: true,
    ...productPromoFetchPolicy,
  });

  const standardFetchPolicy = useStaleFetchPolicy(bootstrapQuery, queryVariables, {
    skip: !runStandardKnownQuery,
  });

  const {
    data: standardData,
    loading: standardLoading,
    refetch: refetchStandard,
  } = useQuery(bootstrapQuery, {
    variables: queryVariables,
    skip: !runStandardKnownQuery,
    notifyOnNetworkStatusChange: true,
    ...standardFetchPolicy,
  });

  const data = isProductPromo
    ? productPromoData?.promotionCampaign?.get
    : (standardData ?? bootstrapData)?.promotionCampaign?.get;

  const loading =
    (runProductPromoQuery && productPromoLoading && !data) ||
    (runStandardKnownQuery && standardLoading && !data) ||
    (!bootstrapSkipped && bootstrapLoading && !data);

  const refetch = isProductPromo ? refetchProductPromo : (refetchStandard ?? refetchBootstrap);

  return {
    loading,
    data: data || [],
    refetch,
  };
};

export const useCheckDuplicateCodes = () => {
  const [pagination, { loading, data }] = useLazyQuery(CHECK_DUPLICATE_CODES);
  const handleCheckDuplicateCodes = async (params) => {
    return pagination({
      variables: params,
    });
  };
  return {
    loading,
    handleCheckDuplicateCodes,
    data: data?.promotionCampaign?.getConditionPromotionProducts,
  };
};

export const useCampaignPermissions = () => {
  const { data: permissions = [], loading } = useGetUserPermissions();
  return {
    canViewListCampaign: hasPermission(permissions, [USER_PERMISSIONS.PROMOTION_VIEW]),
    canViewDetailCampaign: hasPermission(permissions, [USER_PERMISSIONS.PROMOTION_DETAIL_VIEW]),
    canCreateCampaign: hasPermission(permissions, [USER_PERMISSIONS.PROMOTION_CREATE]),
    canUpdateCampaign: hasPermission(permissions, [USER_PERMISSIONS.PROMOTION_UPDATE]),
    loading,
  };
};

export const useGetVouchersCampaign = (params) => {
  const { data, loading, refetch } = useQuery(GET_VOUCHERS_CAMPAIGN, {
    variables: params,
    skip: params?.filters?.promotionCampaignIDs?.length === 0,
  });

  return {
    loading,
    refetch,
    data: data?.promotionCampaign?.getVouchers?.vouchers,
    paginationData: data?.promotionCampaign?.getVouchers?.paginationData,
  };
};

export const useGetVouchersCampaignClone = (params) => {
  const { data, loading, refetch } = useQuery(GET_VOUCHERS_CAMPAIGN_CLONE, {
    variables: params,
    skip: (params?.filters?.promotionCampaignIDs ?? [])?.length === 0,
  });

  return {
    loading,
    refetch,
    data: data?.promotionCampaign?.getVouchers?.vouchers,
    paginationData: data?.promotionCampaign?.getVouchers?.paginationData,
  };
};

export const useActivateVoucher = () => {
  const [activateVoucher, { loading, error }] = useMutation(ACTIVATE_VOUCHER);
  const handleActivateVoucher = async (id) => {
    return await activateVoucher({
      variables: id,
    });
  };

  return {
    handleActivateVoucher,
    loading,
    error,
  };
};

export const useDeactivateVoucher = () => {
  const [deactivateVoucher, { loading, error }] = useMutation(DEACTIVATE_VOUCHER);
  const handleDeactivateVoucher = async (params) => {
    return await deactivateVoucher({
      variables: params,
    });
  };

  return {
    handleDeactivateVoucher,
    loading,
    error,
  };
};

export const useShowVoucherCodes = () => {
  const [showVoucherCodes, { data, loading }] = useLazyQuery(SHOW_VOUCHER_CODES);

  const handleShowVoucherCodes = async (params) => {
    return await showVoucherCodes({
      variables: params,
    });
  };

  return {
    handleShowVoucherCodes,
    loadingShowPassword: loading,
    data,
  };
};

export const useCountVouchers = (params) => {
  const { data, loading, refetch } = useQuery(COUNT_VOUCHERS, {
    variables: params,
  });

  return {
    loading,
    refetch,
    data: data?.promotionCampaign?.countVouchers,
  };
};

export const useGetImportFileCampaign = () => {
  const [getImportFile, { loading, data }] = useLazyQuery(GET_IMPORT_FILE_CAMPAIGN);
  const handleGetImportFile = async (params) => {
    return getImportFile({
      variables: params,
    });
  };
  return {
    loading,
    handleGetImportFile,
    data: data?.promotionCampaign?.getImportFile,
  };
};

export const useGeneratePromotionPreview = () => {
  const [generatePreview, { loading, data }] = useMutation(GENERATE_PROMOTION_PREVIEW);
  const handleGeneratePreview = async (params) => {
    return generatePreview({
      variables: params,
    });
  };
  return {
    loading,
    handleGeneratePreview,
    data: data?.promotionCampaign?.generatePromotionPreview,
  };
};
